'use client';

import Tippy, { TippyProps } from '@tippyjs/react/headless';
import React, { ReactElement, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import './Tooltip.css';

export type TooltipPropTypes = TippyProps & { containerClassName?: string };

/**
 * A tooltip. Provides description or context if it's not obvious what the element in question is doing.
 *
 * Useful with icons or other elements that do not have text.
 *
 * @param content
 * @param containerClassName
 * @param children
 * @param otherTippyProps See https://atomiks.github.io/tippyjs/v6/all-props/
 * @constructor
 */
export default function Tooltip({
  content,
  containerClassName,
  children,
  ...otherTippyProps
}: TooltipPropTypes): ReactElement {
  const [opacity, setOpacity] = useState<number>(
    otherTippyProps.visible ? 100 : 0
  );
  const [timeoutHandler, setTimeoutHandler] = useState<number>(null);
  const durationMs = 300;

  return (
    <Tippy
      animation
      onMount={() => {
        // Cancel unmount if we are triggering the tooltip while it is fading out.
        window.clearTimeout(timeoutHandler);
        setOpacity(100);
      }}
      onHide={({ unmount }) => {
        setTimeoutHandler(window.setTimeout(unmount, durationMs));
        setOpacity(0);
      }}
      render={(attrs) => (
        <div
          className={twMerge(
            `storywind-tooltip bg-black rounded text-white text-sm font-semibold px-3 py-1 transition-opacity opacity-${opacity} duration-${durationMs}`,
            containerClassName
          )}
          tabIndex={-1}
          role="tooltip"
          {...attrs}
        >
          {content}
          <div className="storywind-tooltip-arrow" data-popper-arrow="" />
        </div>
      )}
      {...otherTippyProps}
    >
      {children}
    </Tippy>
  );
}
